import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/**
 * Navigation config a step publishes so the layout can render the Back / Next
 * controls in a single fixed footer (instead of each step rendering its own).
 */
export interface StepNavConfig {
  onBack?: () => void;
  /**
   * Advance handler. May be async (e.g. react-hook-form's `handleSubmit`) so
   * the footer can await validation before reacting to its outcome.
   */
  onNext?: () => void | Promise<void>;
  nextLabel?: string;
  isSubmitting?: boolean;
  hideBack?: boolean;
  hideNext?: boolean;
  nextDisabled?: boolean;
}

const StepNavValueContext = createContext<StepNavConfig | null>(null);
const StepNavSetContext = createContext<(config: StepNavConfig | null) => void>(
  () => {},
);

/**
 * Holds the current step's footer navigation config. Split into a value
 * context (consumed by the footer) and a stable setter context (used by steps)
 * so that a step registering its nav does not re-render on footer changes.
 */
export const StepNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [nav, setNav] = useState<StepNavConfig | null>(null);
  return (
    <StepNavSetContext.Provider value={setNav}>
      <StepNavValueContext.Provider value={nav}>
        {children}
      </StepNavValueContext.Provider>
    </StepNavSetContext.Provider>
  );
};

/** Read the current footer navigation config (used by the layout footer). */
export const useStepNavValue = (): StepNavConfig | null =>
  useContext(StepNavValueContext);

/**
 * Publish the current step's footer navigation. Handlers are stored in refs so
 * the footer always calls the freshest closure without re-registering on every
 * render; only the visual props (labels, submitting/disabled flags) are effect
 * dependencies.
 */
export const useRegisterStepNav = (config: StepNavConfig): void => {
  const setNav = useContext(StepNavSetContext);

  const onNextRef = useRef(config.onNext);
  onNextRef.current = config.onNext;
  const onBackRef = useRef(config.onBack);
  onBackRef.current = config.onBack;

  const { nextLabel, isSubmitting, hideBack, hideNext, nextDisabled } = config;

  useLayoutEffect(() => {
    setNav({
      onNext: () => onNextRef.current?.(),
      onBack: () => onBackRef.current?.(),
      nextLabel,
      isSubmitting,
      hideBack,
      hideNext,
      nextDisabled,
    });
    // Intentionally no cleanup that clears the config: with AnimatePresence
    // `mode="wait"` the next step registers right after the previous unmounts,
    // so leaving the last config in place avoids a one-frame empty footer.
  }, [setNav, nextLabel, isSubmitting, hideBack, hideNext, nextDisabled]);
};
